/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */

import styled from 'styled-components';

import { fonts, input } from '../../../../theme';

/**
 * @description Style props for input container and its children.
 * @interface IStyledProps
 * @extends React.Props<any>
 * @see IInputProps
 *
 * @property {string} cursor Define cursor style for input field and its icons.
 * @property {string} font Define font style for input field.
 * @property {number} iconSize Define size in px for left or right icons.
 * @property {number} inputHeightDefine input height according to its type.
 * @property {string} kind Define style according to  theme settings.
 * @property {string} leftIcon Define path to left icon.
 * @property {number} paddingLeft Define padding-left for input container. Change on icons position
 * @property {number} paddingRight Define padding-right for input container.
 * @property {string} rightIcon Define path to right icon.
 */
interface IStyledProps {
    cursor?: string;
    font?: string;
    iconSize?: number;
    inputHeight?: number;
    kind?: string;
    leftIcon?: string;
    paddingLeft?: number;
    paddingRight?: number;
    positionMargin?: number;
    rightIcon?: string;
}

interface ILabelProps {
    kind?: string;
    labelFont?: string;
}

const InputContainerStyled = styled.div<IStyledProps>`
    position: relative;
    width: 100%;
    box-sizing: border-box;
    & textarea,
    & input {
        width: 100%;
        line-height: ${(props) => props.inputHeight}px;
        height: ${(props) => props.inputHeight}px;
        border: solid 1px ${input.borderColor};
        padding-left: ${(props) => props.paddingLeft}px;
        padding-right: ${(props) => props.paddingRight}px;
        box-sizing: border-box;
        cursor: ${(props) => props.cursor};
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        ${(props) => props.font};
        color: ${input.fontColor};
        background-color: ${input.backgroundColor};
        ::placeholder {
            color: ${input.placeholderFontColor};
        }

        ::-webkit-input-placeholder {
            color: ${input.placeholderFontColor};
        }

        :-moz-placeholder {
            color: ${input.placeholderFontColor};
        }
        ::-moz-placeholder {
            color: ${input.placeholderFontColor};
        }
        ::-ms-input-placeholder {
            color: ${input.placeholderFontColor};
        }
        :-ms-input-placeholder {
            color: ${input.placeholderFontColor};
        }
        :focus {
            outline: none;
        }
        ::-ms-clear,
        ::-ms-reveal {
            display: none;
        }
        :disabled {
            color: ${input.fontColorDisable};
            border-color: ${input.borderColorDisable};
            background-color: ${input.backgroundColorDisable};
        }
        border: ${(props) => (props.kind === 'noborder' ? 'none' : '')};
    }

    & textarea {
        white-space: normal;
        overflow: auto;
        resize: none;
    }

    .table-filter_filter-field .drop-down-filter__content & input.input_filled {
        border-color: ${input.borderColor};
    }

    .select_select-opened &.select__select-input input,
    .drop-down-filter_opened &.drop-down-filter__drop-down-input input,
    .multiselect_select-opened &.multiselect__select-input input,
    .table-filter_filter-field & input.input_filled {
        border-color: ${input.borderColorFocus};
    }

    &:not(.select__select-input):not(.select__select-input):not(.drop-down-filter__drop-down-input) {
        input:focus,
        textarea:focus {
            color: ${input.fontColorFocus};
            border-color: ${input.borderColorFocus};
            background-color: ${input.backgroundColorFocus};
        }
    }
    &.drop-down-filter__drop-down-input,
    &.select__select-input > * {
        display: none;
    }
`;

const InputWithIconStyled = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

const InputIconStyled = styled.img<IStyledProps>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

const InputIconLeftStyled = styled(InputIconStyled).attrs({
    'data-input-icon-left': 'inputIconLeft',
})`
    width: ${(props) => props.iconSize}px;
    left: ${(props) => props.positionMargin}px;
`;

const InputIconRightStyled = styled(InputIconStyled).attrs({
    'data-input-icon-right': 'inputIconRight',
})`
    width: ${(props) => props.iconSize}px;
    right: ${(props) => props.positionMargin}px;
`;

interface IInputHintStyled {
    kind?: string;
}

const InputHintStyled = styled.div<IInputHintStyled>`
    margin-top: 4px;
    white-space: nowrap;
    ${fonts.textSmall};
    color: ${input.hintFontColor};
`;

const LabelStyled = styled.label<ILabelProps>`
    display: block;
    margin-bottom: 4px;
    color: ${input.labelColor};
    text-align: left;
    user-select: none;
    ${(props) => props.labelFont}
`;

const LabelMarkStyled = styled.span`
    margin-left: 4px;
    width: 7px;
    height: 24px;
    color: red;
`;

const LabelHintStyled = styled.div`
    margin-bottom: 4px;
    text-align: left;
    user-select: none;
`;

export {
    InputContainerStyled,
    InputIconStyled,
    InputIconLeftStyled,
    InputIconRightStyled,
    InputHintStyled,
    InputWithIconStyled,
    LabelStyled,
    LabelMarkStyled,
    LabelHintStyled,
};
